import { ColumnDef } from '@tanstack/react-table';
import { NewTableClasses } from './components/table-view';

export type NewTableDefaultDataItem = Record<string, any>;

export type NewTableRenderHeaderProps = {};

export type NewTableColumnFilterProps = {
  type: NewTableFilterType;
  options?: Array<string>;
  outputDateFormat?: string;
};

export type NewTableColumn<DataItem> = {
  id: string;
  disableActions?: boolean;
  className?: string;
  accessorKey: string;
  cell?: ColumnDef<DataItem>['cell'];
  enableResizing?: boolean;
  enableSorting?: boolean;
  header?: ColumnDef<DataItem>['header'];
  maxSize?: number;
  minSize?: number;
  size?: number;
  filterProps?: NewTableColumnFilterProps;
};

export type NewTableColumnId = NewTableColumn<NewTableDefaultDataItem>['id'];

export enum NewTableSortingDirection {
  desc = 'desc',
  asc = 'asc',
};

export type NewTableLoadMetadataSorting = {
  id: string;
  direction: NewTableSortingDirection;
};

export type NewTableLoadMetadataPagination = {
  start: number;
  size: number;
};

export enum NewTableFilterApplyType {
	and = '0',
	or = '1',
}

export type NewTableFilterValue = {
	operator1?: number | string;
	whereClause1?: number | string;
	andOR?: NewTableFilterApplyType;
	operator2?: number | string;
	whereClause2?: number | string;
};

export type NewTableLoadMetadataFiltering = {
  filter: boolean;
  columns: Record<NewTableColumnId, NewTableFilterValue>;
};

export type NewTableLoadMetadata = {
  filtering?: NewTableLoadMetadataFiltering;
  sorting: NewTableLoadMetadataSorting;
  pagination: NewTableLoadMetadataPagination;
};

type NewTableFilterId = number | string;
type NewTableFilterName = string;

export type NewTableFilters = {
  operators: Record<NewTableFilterId, NewTableFilterName>;
};

export enum NewTableColumnPredefinedActionType {
  filters ='filters',
  deleteColumn ='deleteColumn',
  addColumn ='addColumn',
};

export type NewTableColumnAction = {
  className?: string;
  label?: string;
  type?: NewTableColumnPredefinedActionType;
  onClick?: (params?: { columnId?: NewTableColumnId }) => void;
};

export enum NewTableHeaderPredefinedActionType {
  addColumn ='addColumn',
};

export type NewTableHeaderAction = {
  className?: string;
  label?: string;
  type?: NewTableHeaderPredefinedActionType;
  onClick?: () => void;
};

export type NewTableRowAction = {
  label?: string;
  onClick?: () => void;
};

export type NewTableFolder = {
  name: string;
  parentid: number;
  secToken: number;
  shortcut?: boolean;
  idstemplate?: boolean;
  pdstemplate?: boolean;
  childrenLoaded?: boolean;
  readAllowed?: boolean;
  writeAllowed?: boolean;
  system: boolean;
  context: number;
  hasChildren?: boolean;
  id: number;
  children?: Array<NewTableFolder>;
  imageURL: string;
  wdcenabled?: boolean;
  wdcfolder?: boolean;
  childrenForDisplay?: Array<NewTableFolder>;
  imageURLSelected: string;
};

export type NewTableField = {
  fieldId: number;
  fieldName: string;
  fieldData?: unknown;
  fieldContext: number;
  fieldTypeId: number;
  fieldTypeName: string;
  systemField: boolean;
  columnWidth: number;
  imageURL: string;
};

export type NewTableSelection = {
  ids: Array<string | number>;
  onChange: (ids: Array<string | number>) => void;
};

export type NewTableColumnsResizing = {
  onChange?: (columns: Record<NewTableColumnId, number>) => void;
};

export type NewTableColumnsManagement = {
  onAdd: (
    data: {
      field: NewTableField;
      folder: NewTableFolder;
      columnId: NewTableColumnId;
    }
  ) => Promise<void>;
  onRemove: (columnId: NewTableColumnId) => Promise<void>;
  getFolders: () => Promise<Array<NewTableFolder>>;
  getFields: (params: {
    searchKeyword: string;
    folderId: NewTableFolder['id'];
    system: boolean;
  }) => Promise<Array<NewTableField>>;
};

export enum NewTableFilterType {
  text = 1,
  date = 2,
  number = 3,
  checkbox = 4,
  dropdown = 5,
  image = 6,
  lookupTable = 7,
  table = 8,
  computed = 9,
  summary = 11,
  tableSummary = 13,
  hyperlink = 14,
  integer = 30,
};

export type NewTableProps<DataItem = NewTableDefaultDataItem> = {
  infiniteScroll?: boolean;
  disableAutoInit?: boolean;
  columnActions?: Array<NewTableColumnAction>;
  headerActions?: Array<NewTableHeaderAction>;
  rowActions?: Array<NewTableRowAction>;
  loadItemsCount?: number;
  totalItemsCount?: number;
  columnsResizing?: NewTableColumnsResizing;
  columnsManagement?: NewTableColumnsManagement;
  selection?: NewTableSelection;
  itemUniqProperty: string;
  classes?: NewTableClasses;
  filters?: NewTableFilters;
  moduleName: string;
  noDataMessage: React.ReactNode;
  /** @description required if pagination functionality is on */ 
  loadMetadata?: NewTableLoadMetadata;
  data: Array<DataItem>;
  columns: Array<NewTableColumn<DataItem>>;
  onLoadData?: (loadMetadata: NewTableLoadMetadata) => Promise<void> | void;
};